import { action, observable, runInAction } from "mobx";
import { InputMode } from "../Types";
import {
  inputModeComputed,
  isShowingNonFocusedPopoverBox,
} from "./CanvasState";
import { ParentIframeEventEmitter } from "./FrameSandboxAPI";
import { hasFocusGuardOpen } from "./SandboxUtils";

export const keydownCodeSet = observable.set<string>();

type Modifier = "Ctrl" | "Shift" | "Alt" | "Meta";
const MODIFIER_KEYS: Modifier[] = ["Ctrl", "Shift", "Alt", "Meta"];
export function getKeyWithModifier(key: string, modifiers: Modifier[]) {
  return `${MODIFIER_KEYS.flatMap((modifier) =>
    modifiers.includes(modifier) ? `${modifier}-` : ""
  ).join("")}${key}`;
}

export enum KeyboardCommandPriority {
  Normal = 1,
  High = 2,
}
export type KeyboardCommand = {
  name: string;
  keys: string[] | "*";
  modes?: InputMode[];
  // while space is down, allow handling arrow keys
  allowOtherNonModifierKeys?: true;
  allowRepeat?: true;
  priority?: KeyboardCommandPriority;
  handler: (key: string, e?: KeyboardEvent) => void | false;
};
export const keyboardCommandsMobx = observable.array<KeyboardCommand>([], {
  deep: false,
});

export function registerKeyboardCommand(command: KeyboardCommand): () => void {
  runInAction(() => {
    if (command.priority === KeyboardCommandPriority.High) {
      keyboardCommandsMobx.unshift(command);
    } else {
      keyboardCommandsMobx.push(command);
    }
  });
  return action(() => {
    const index = keyboardCommandsMobx.indexOf(command);
    if (index !== -1) {
      keyboardCommandsMobx.splice(index, 1);
    }
  });
}

export function registerKeyboardCommands(
  commands: KeyboardCommand[]
): () => void {
  const unsubscribes = commands.map((command) =>
    registerKeyboardCommand(command)
  );
  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}

function shouldSkipHandlingEvent(e: KeyboardEvent) {
  if (
    !(e.ctrlKey && e.metaKey) &&
    e.target instanceof HTMLElement &&
    (e.target.tagName === "TEXTAREA" ||
      e.target.tagName === "INPUT" ||
      e.target.getAttribute("contenteditable") === "true")
  ) {
    return true;
  }
  return !isShowingNonFocusedPopoverBox.get() && hasFocusGuardOpen();
}

function processCode(code: string) {
  switch (code) {
    case "MetaLeft":
    case "MetaRight":
      return "Meta";
    case "AltLeft":
    case "AltRight":
      return "Alt";
    case "ControlLeft":
    case "ControlRight":
      return "Ctrl";
    case "ShiftLeft":
    case "ShiftRight":
      return "Shift";
    default:
      return code;
  }
}

let currentKeySequence: string[] = [];
let clearCurrentKeySequenceTimeout: number | undefined = undefined;

function handleKeyDown(
  key: string,
  code: string,
  altKey: boolean,
  metaKey: boolean,
  ctrlKey: boolean,
  shiftKey: boolean,
  isRepeat: boolean,
  e?: KeyboardEvent
) {
  runInAction(() => {
    keydownCodeSet.add(processCode(code));
  });
  if (MODIFIER_KEYS.includes(key as Modifier) || key === "Control") {
    return;
  }
  const keyWithModifier = getKeyWithModifier(
    key,
    <Modifier[]>(
      [
        ctrlKey ? "Ctrl" : undefined,
        altKey ? "Alt" : undefined,
        shiftKey ? "Shift" : undefined,
        metaKey ? "Meta" : undefined,
      ].filter(Boolean)
    )
  );
  const numNonModifierKeysDown = [...keydownCodeSet.values()].filter(
    (c) => !MODIFIER_KEYS.includes(c as Modifier)
  ).length;
  currentKeySequence.push(keyWithModifier);
  const currentKeySequenceString = currentKeySequence.join(" ");
  if (clearCurrentKeySequenceTimeout !== undefined) {
    clearTimeout(clearCurrentKeySequenceTimeout);
    clearCurrentKeySequenceTimeout = undefined;
  }
  for (const command of keyboardCommandsMobx) {
    if (
      currentKeySequence.length === 1 &&
      numNonModifierKeysDown > 1 &&
      command.allowOtherNonModifierKeys !== true
    ) {
      continue;
    }
    if (isRepeat && command.allowRepeat !== true) {
      continue;
    }
    if (
      (command.keys === "*" ||
        command.keys.includes(currentKeySequenceString)) &&
      ((command.modes === undefined &&
        inputModeComputed.get() === InputMode.Normal) ||
        command.modes?.includes(inputModeComputed.get()))
    ) {
      if (command.handler(currentKeySequenceString, e) !== false) {
        currentKeySequence = [];
        return;
      }
    }
  }
  clearCurrentKeySequenceTimeout = setTimeout(() => {
    currentKeySequence = [];
    clearCurrentKeySequenceTimeout = undefined;
  }, 300);
}
function handleKeyUp(key: string, code: string) {
  const processedCode = processCode(code);
  runInAction(() => {
    if (processedCode === "Meta") {
      // on MacOS, keyup events are not fired when cmd key is down
      // just reset keydownCodeSet so we don't get stuck with a key
      keydownCodeSet.clear();
    } else {
      keydownCodeSet.delete(processedCode);
    }
  });
}

function onKeyDown(e: KeyboardEvent) {
  if (shouldSkipHandlingEvent(e)) {
    return;
  }
  handleKeyDown(
    e.key,
    e.code,
    e.altKey,
    e.metaKey,
    e.ctrlKey,
    e.shiftKey,
    e.repeat,
    e
  );
}
function onParentBodyKeyDown(e: {
  key: string;
  code: string;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}) {
  handleKeyDown(
    e.key,
    e.code,
    e.altKey,
    e.metaKey,
    e.ctrlKey,
    e.shiftKey,
    false
  );
}

function onKeyUp(e: KeyboardEvent) {
  handleKeyUp(e.key, e.code);
}
function onParentBodyKeyUp(e: {
  key: string;
  code: string;
  altKey: boolean;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}) {
  handleKeyUp(e.key, e.code);
}

function onBlur() {
  runInAction(() => {
    keydownCodeSet.clear();
  });
}

export function setupKeyboardListeners() {
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("blur", onBlur);
  ParentIframeEventEmitter.addListener("BODY_KEYDOWN", onParentBodyKeyDown);
  ParentIframeEventEmitter.addListener("BODY_KEYUP", onParentBodyKeyUp);

  return () => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    window.removeEventListener("blur", onBlur);
    ParentIframeEventEmitter.removeListener(
      "BODY_KEYDOWN",
      onParentBodyKeyDown
    );
    ParentIframeEventEmitter.removeListener("BODY_KEYUP", onParentBodyKeyUp);
  };
}
